// True-preview surface (docs/10 §2.4): mounts the SHARED player-core playback
// engine so what an editor sees is exactly what a screen plays — real durations,
// real crossfades, the real overlay. The engine is DOM-based and side-effect-free
// (media via a plain-URL MediaSource, telemetry dropped); we only own the box it
// renders into.
//
// The media source rewrites relative `/v1/media/:id/file` urls to the API's own
// absolute origin (resolvePreviewMediaUrl, api/media.ts) before handing them to the
// engine as <img>/<video>/<iframe> src. Both preview manifests it's fed carry that
// relative shape by design (this CMS's own preview-manifest.ts and the backend's
// toSessionManifest for screen "Preview now") — without this, those elements resolve
// against the CMS's OWN origin in a deployed build and every media kind 404s. The
// session cookie is SameSite=Lax on a shared seencast.app registrable domain, so it
// still attaches to these cross-origin-but-same-site requests; no token scheme needed.
//
// mountPreview treats the element we pass as the HOST: it sets overflow:hidden and
// creates an embedded-safe `.stage` (position:absolute; inset:0) inside it, so the
// preview stays boxed on its own. `.engine-frame` (app.css) is the positioned 16:9
// container; we mount into a throwaway `.engine-mount` child that fills it, so unmount
// tears the engine's whole DOM down in one `.remove()` (StrictMode-safe: mount→cleanup
// →mount leaves nothing behind).

import { useEffect, useRef } from 'react';
import {
  mountPreview,
  activeItems,
  PlainUrlMediaSource,
  type Manifest,
  type PreviewHandle,
} from '@seencast/player-core';
import { resolvePreviewMediaUrl } from '../api/media.ts';

// Stateless (a pure rewrite fn) — one instance covers every EnginePreview mount.
const previewMedia = new PlainUrlMediaSource(resolvePreviewMediaUrl);

export function EnginePreview({
  manifest,
  emptyLabel = 'Nothing to preview yet',
  ariaLabel = 'Live preview',
  fill = false,
}: {
  /** The manifest to play, or null while it loads. */
  manifest: Manifest | null;
  /** Shown (instead of the engine) when there is nothing to play right now. */
  emptyLabel?: string;
  ariaLabel?: string;
  /** Fill the parent box (position:absolute inset:0) instead of the standalone
   *  16:9 frame — used to tile a zone inside a multi-zone screen preview. */
  fill?: boolean;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<PreviewHandle | null>(null);

  // Nothing plays now (no active window items and no fallback) → an honest empty
  // state rather than the engine's black idle screen. Uses the engine's own
  // timeline logic so "empty" means exactly what the player would treat as idle.
  const hasItems = manifest ? activeItems(manifest, Date.now()).length > 0 : false;

  // Mount the engine when we have something to play; unmount when we don't. Live
  // manifest edits do NOT remount — they flow through the swap effect below.
  useEffect(() => {
    if (!hasItems || !manifest) return;
    const host = hostRef.current;
    if (!host) return;
    const mount = document.createElement('div');
    mount.className = 'engine-mount';
    host.appendChild(mount);
    const handle = mountPreview(mount, manifest, { media: previewMedia });
    handleRef.current = handle;
    return () => {
      handle.stop();
      handleRef.current = null;
      mount.remove(); // drop the engine's whole DOM subtree (stage/layers/overlay)
    };
    // `manifest` is intentionally not a dep: the initial manifest is captured on
    // mount, and every later edit is applied via setManifest (no remount / reflow).
  }, [hasItems]);

  // Atomic manifest swap into the running engine (overlay included). Ordered after
  // the mount effect, so handleRef is set before the first swap fires.
  useEffect(() => {
    if (manifest && handleRef.current) handleRef.current.setManifest(manifest);
  }, [manifest]);

  const rootClass = fill ? 'engine-zone' : 'engine-frame';
  if (!hasItems) {
    return (
      <div className={rootClass}>
        <div className="engine-empty">{emptyLabel}</div>
      </div>
    );
  }
  return <div className={rootClass} ref={hostRef} role="img" aria-label={ariaLabel} />;
}
